import { useQuery } from "@tanstack/react-query";
import { GLAccount } from "@/types/integration";
import { useGetBackendUrl } from "./use-get-backend-url";
import { useAuth } from "@/providers/AuthProvider";

/**
 * Hook to fetch GL (General Ledger) accounts from the ERP system
 * Returns accounts when integration is active
 */
export function useGLAccounts(enabled: boolean = true) {
  const backendUrl = useGetBackendUrl();
  const { instanceId } = useAuth();

  return useQuery({
    queryKey: ["gl-accounts", instanceId],
    queryFn: async (): Promise<GLAccount[]> => {
      try {
        const headers: Record<string, string> = {};
        if (instanceId) {
          headers["Instance-Id"] = instanceId;
        }
        const response = await fetch(`${backendUrl}/api/integration/gl-accounts`, { headers });
        
        if (!response.ok) {
          throw new Error("Failed to fetch GL accounts");
        }
        
        return await response.json();
      } catch (error) {
        console.error("Error fetching GL accounts:", error);
        // Return mock data as fallback for demo purposes
        return getMockGLAccounts();
      }
    },
    enabled: enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Mock GL accounts for demo/fallback purposes
 */
function getMockGLAccounts(): GLAccount[] {
  return [
    { code: "1000", description: "Cash in Bank", accountType: "asset", category: "Current Assets", isActive: true },
    { code: "1100", description: "Accounts Receivable", accountType: "asset", category: "Current Assets", isActive: true },
    { code: "1200", description: "Inventory - Raw Materials", accountType: "asset", category: "Current Assets", isActive: true },
    { code: "1210", description: "Inventory - Finished Goods", accountType: "asset", category: "Current Assets", isActive: true },
    { code: "1300", description: "Prepaid Expenses", accountType: "asset", category: "Current Assets", isActive: true },
    { code: "1500", description: "Fixed Assets - Equipment", accountType: "asset", category: "Fixed Assets", isActive: true },
    { code: "2000", description: "Accounts Payable", accountType: "liability", category: "Current Liabilities", isActive: true },
    { code: "2100", description: "Accrued Expenses", accountType: "liability", category: "Current Liabilities", isActive: true },
    { code: "2200", description: "Short-term Loans", accountType: "liability", category: "Current Liabilities", isActive: true },
    { code: "3000", description: "Owner's Equity", accountType: "equity", category: "Equity", isActive: true },
    { code: "3100", description: "Retained Earnings", accountType: "equity", category: "Equity", isActive: true },
    { code: "4000", description: "Sales Revenue", accountType: "revenue", category: "Operating Revenue", isActive: true },
    { code: "4100", description: "Service Revenue", accountType: "revenue", category: "Operating Revenue", isActive: true },
    { code: "4200", description: "Other Income", accountType: "revenue", category: "Non-Operating Revenue", isActive: true },
    { code: "5000", description: "Cost of Goods Sold", accountType: "expense", category: "Cost of Sales", isActive: true },
    { code: "5100", description: "Purchase Returns", accountType: "expense", category: "Cost of Sales", isActive: true },
    { code: "6000", description: "Salaries and Wages", accountType: "expense", category: "Operating Expenses", isActive: true },
    { code: "6100", description: "Rent Expense", accountType: "expense", category: "Operating Expenses", isActive: true },
    { code: "6200", description: "Utilities Expense", accountType: "expense", category: "Operating Expenses", isActive: true },
    { code: "6300", description: "Marketing and Advertising", accountType: "expense", category: "Operating Expenses", isActive: true },
    { code: "6400", description: "Office Supplies", accountType: "expense", category: "Operating Expenses", isActive: true },
    { code: "6500", description: "Depreciation Expense", accountType: "expense", category: "Operating Expenses", isActive: true },
    { code: "6600", description: "Insurance Expense", accountType: "expense", category: "Operating Expenses", isActive: true },
    { code: "6700", description: "Professional Fees", accountType: "expense", category: "Operating Expenses", isActive: true },
    { code: "6800", description: "Travel and Entertainment", accountType: "expense", category: "Operating Expenses", isActive: true },
  ];
}
